import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { toastService } from '../../../../utils/toastService';
import { useLazyGetAvailableChassisQuery } from '../../../../api/endpoints/vehicleOrder';

const ChassisSelectionDialog = ({ visible, onHide, orderId, quantity = 1, refreshKey = 0, onConfirm, isLoading = false }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const [fetchChassis, { data: chassisData, isFetching: chassisLoading }] = useLazyGetAvailableChassisQuery();
    const chassisList = useMemo(() => chassisData ?? [], [chassisData]);
    const [selections, setSelections] = useState([]);
    const isChassisInsufficient = !chassisLoading && chassisList.length < quantity;

    useEffect(() => {
        if (visible && orderId) {
            setSelections(Array(quantity).fill(null));
        }
    }, [visible, orderId, quantity]);

    // Modal her açıldığında ve onay hatasından sonra (refreshKey) liste yeniden çekilir
    useEffect(() => {
        if (visible && orderId) {
            fetchChassis({ orderId, languageId });
        }
    }, [visible, orderId, refreshKey, fetchChassis, languageId]);

    // Yenilenen listede artık bulunmayan (başka siparişe eşleşmiş) seçimleri temizle
    useEffect(() => {
        const availableIds = new Set(chassisList.map((c) => c.id));
        setSelections((prev) => (prev.some((s) => s && !availableIds.has(s.id)) ? prev.map((s) => (s && availableIds.has(s.id) ? s : null)) : prev));
    }, [chassisList]);

    const handleSelectionChange = (index, value) => {
        setSelections((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    const getAvailableOptions = (currentIndex) => {
        const selectedIds = selections
            .filter((_, i) => i !== currentIndex)
            .filter(Boolean)
            .map((s) => s.id);
        return chassisList.filter((c) => !selectedIds.includes(c.id));
    };

    const allSelected = useMemo(() => {
        return selections.length === quantity && selections.every(Boolean);
    }, [selections, quantity]);

    const handleConfirm = () => {
        if (!allSelected) {
            toastService.error(th('vehicleSalesOrderRequests.chassisSelection.allRequired'));
            return;
        }

        const ids = selections.map((s) => s.id);
        const hasDuplicate = new Set(ids).size !== ids.length;
        if (hasDuplicate) {
            toastService.error(th('vehicleSalesOrderRequests.chassisSelection.duplicateError'));
            return;
        }

        const selectedChassis = selections.map((s) => ({
            charg: s.charg,
            zz1SasinumarasiSdh: s.zz1SasinumarasiSdh
        }));

        onConfirm?.(selectedChassis);
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label={th('vehicleSalesOrderRequests.buttons.cancel')}
                icon="pi pi-times"
                className="p-button-text"
                onClick={onHide}
                disabled={isLoading}
            />
            <Button
                label={th('vehicleSalesOrderRequests.buttons.confirm')}
                icon="pi pi-check"
                className="p-button-success"
                onClick={handleConfirm}
                loading={isLoading}
                disabled={!allSelected}
            />
        </div>
    );

    return (
        <Dialog
            header={th('vehicleSalesOrderRequests.chassisSelection.title')}
            visible={visible}
            onHide={onHide}
            style={{ width: '600px' }}
            footer={footer}
            modal
        >
            {chassisLoading ? (
                <div className="flex justify-content-center p-4">
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="flex flex-column gap-3">
                    <p className="text-color-secondary mt-0">
                        {th('vehicleSalesOrderRequests.chassisSelection.description')}
                    </p>

                    {isChassisInsufficient && (
                        <Message
                            severity="warn"
                            className="w-full justify-content-start"
                            text={th('vehicleSalesOrderRequests.chassisSelection.insufficientChassis', { available: chassisList.length, required: quantity })}
                        />
                    )}

                    {Array.from({ length: quantity }).map((_, index) => (
                        <div key={index} className="field">
                            <label className="font-bold block mb-2">
                                {th('vehicleSalesOrderRequests.chassisSelection.chassisLabel')} {index + 1}
                            </label>
                            <Dropdown
                                value={selections[index]}
                                options={getAvailableOptions(index)}
                                optionLabel="displayText"
                                onChange={(e) => handleSelectionChange(index, e.value)}
                                placeholder={th('vehicleSalesOrderRequests.chassisSelection.selectChassis')}
                                className="w-full"
                                filter
                            />
                        </div>
                    ))}
                </div>
            )}
        </Dialog>
    );
};

export default ChassisSelectionDialog;
